import React, { useState } from "react";
import styled from "styled-components";
import { Form, Input, Button, Checkbox, message } from "antd";
import { Helmet } from "react-helmet";
import { CheckCircleOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { submitVendorApply } from "../../lib/api/vendorApplyApi";
import { APP_NAME } from "../../helpers/constants";

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    165deg,
    #f0fdfa 0%,
    #ecfeff 35%,
    #f8fafc 70%,
    #e0f2fe 100%
  );
  padding: clamp(2rem, 5vw, 4rem) 1.25rem 4rem;
`;

const Shell = styled.div`
  max-width: 520px;
  margin: 0 auto;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  margin-bottom: 1.75rem;
`;

const BrandMark = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(145deg, #0d9488 0%, #0e7490 45%, #0369a1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(13, 148, 136, 0.35);
`;

const BrandText = styled.div`
  text-align: left;
`;

const BrandName = styled.div`
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f172a;
`;

const BrandTag = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #0d9488;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 20px;
  padding: clamp(1.75rem, 4vw, 2.25rem);
  box-shadow:
    0 1px 0 rgba(15, 23, 42, 0.04),
    0 24px 48px -12px rgba(15, 118, 110, 0.12),
    0 0 0 1px rgba(15, 23, 42, 0.06);
`;

const Headline = styled.h1`
  margin: 0 0 0.5rem;
  font-size: clamp(1.45rem, 4vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #0f172a;
  line-height: 1.25;
`;

const Subhead = styled.p`
  margin: 0 0 1.75rem;
  font-size: 0.95rem;
  line-height: 1.55;
  color: #475569;
`;

const TrustStrip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #f0fdfa 0%, #ecfeff 100%);
  border: 1px solid rgba(13, 148, 136, 0.2);
  margin-bottom: 1.5rem;
  font-size: 0.82rem;
  line-height: 1.45;
  color: #134e4a;
`;

const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    color: #334155 !important;
    font-weight: 600;
    font-size: 0.875rem;
  }
  .ant-input,
  .ant-input-affix-wrapper {
    border-radius: 10px !important;
    border-color: #cbd5e1 !important;
    padding: 0.5rem 0.85rem;
  }
  .ant-input:hover,
  .ant-input-affix-wrapper:hover {
    border-color: #94a3b8 !important;
  }
  .ant-input:focus,
  .ant-input-affix-wrapper-focused {
    border-color: #0d9488 !important;
    box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.15) !important;
  }
`;

const SubmitButton = styled(Button)`
  height: 48px !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  font-size: 1rem !important;
  border: none !important;
  background: linear-gradient(135deg, #0d9488 0%, #0e7490 50%, #0284c7 100%) !important;
  box-shadow: 0 10px 28px -6px rgba(13, 148, 136, 0.55) !important;
  &:hover,
  &:focus {
    background: linear-gradient(135deg, #0f766e 0%, #155e75 50%, #0369a1 100%) !important;
    opacity: 0.96;
  }
`;

const FooterNote = styled.p`
  margin: 1.5rem 0 0;
  text-align: center;
  font-size: 0.8rem;
  color: #64748b;
  a {
    color: #0d9488;
    font-weight: 500;
  }
`;

const SuccessPanel = styled.div`
  text-align: center;
  padding: 1rem 0 0.5rem;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: clamp(1.45rem, 4vw, 1.75rem);
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #0f172a;
  line-height: 1.25;
`;

const VendorApplyPage = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onFinish = async (values) => {
    setSubmitting(true);
    try {
      await submitVendorApply({
        name: values.name,
        trade: values.trade,
        licenseNumber: values.licenseNumber || "",
        zipCode: values.zipCode,
        email: values.email,
        leoPreferredVendorInterest: Boolean(values.leoPreferredVendorInterest),
      });
      message.success("Application received.");
      setDone(true);
    } catch (e) {
      message.error(e?.message || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Helmet>
        <title>Contractor network | {APP_NAME}</title>
        <meta
          name="description"
          content="Apply to join the LandlordOps contractor network. Licensed trades trusted by property owners."
        />
      </Helmet>
      <Shell>
        <BrandRow>
          <BrandMark aria-hidden>
            <SafetyCertificateOutlined
              style={{ fontSize: 22, color: "#fff" }}
            />
          </BrandMark>
          <BrandText>
            <BrandName>{APP_NAME}</BrandName>
            <BrandTag>Contractor network</BrandTag>
          </BrandText>
        </BrandRow>

        <Card>
          {done ? (
            <SuccessPanel>
              <CheckCircleOutlined
                style={{
                  fontSize: 52,
                  color: "#0d9488",
                  marginBottom: "1rem",
                }}
              />
              <SuccessTitle>Thank you</SuccessTitle>
              <Subhead style={{ marginBottom: 0 }}>
                We&apos;ve received your details. If your service area aligns
                with active LandlordOps properties, our team will reach out
                shortly.
              </Subhead>
            </SuccessPanel>
          ) : (
            <>
              <Headline>Partner with property owners who value quality</Headline>
              <Subhead>
                LandlordOps connects professional contractors with
                self-managing landlords. Tell us about your business. We review
                every application.
              </Subhead>
              <TrustStrip>
                <SafetyCertificateOutlined
                  style={{ fontSize: 18, color: "#0d9488", marginTop: 2 }}
                />
                <span>
                  We work with licensed, insured trades. Your information is
                  used only to evaluate network fit and will never be sold.
                </span>
              </TrustStrip>

              <StyledForm
                form={form}
                layout="vertical"
                requiredMark={false}
                initialValues={{ leoPreferredVendorInterest: false }}
                onFinish={onFinish}
              >
                <Form.Item
                  name="name"
                  label="Full name"
                  rules={[
                    { required: true, message: "Please enter your name" },
                    { min: 2, message: "Name is too short" },
                  ]}
                >
                  <Input placeholder="Alex Rivera" autoComplete="name" />
                </Form.Item>
                <Form.Item
                  name="trade"
                  label="Trade / specialty"
                  rules={[
                    { required: true, message: "Please enter your trade" },
                    { min: 2, message: "Please add a bit more detail" },
                  ]}
                >
                  <Input placeholder="e.g. Licensed electrician, commercial & residential" />
                </Form.Item>
                <Form.Item
                  name="licenseNumber"
                  label="License number"
                  rules={[{ max: 80, message: "Too long" }]}
                >
                  <Input placeholder="State license or certification #" />
                </Form.Item>
                <Form.Item
                  name="zipCode"
                  label="Primary service ZIP code"
                  rules={[
                    { required: true, message: "ZIP code is required" },
                    {
                      pattern: /^\d{5}(-\d{4})?$/,
                      message: "Use a 5-digit U.S. ZIP (optional +4)",
                    },
                  ]}
                >
                  <Input placeholder="97201" maxLength={10} inputMode="numeric" />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Business email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter a valid email" },
                  ]}
                >
                  <Input placeholder="you@yourcompany.com" autoComplete="email" />
                </Form.Item>
                <Form.Item
                  name="leoPreferredVendorInterest"
                  valuePropName="checked"
                  style={{ marginBottom: "1.25rem" }}
                >
                  <Checkbox style={{ color: "#334155", lineHeight: 1.5 }}>
                    I am interested in becoming a{" "}
                    <strong>Leo AI Verified Preferred Vendor</strong> for emergency
                    calls in my area.
                  </Checkbox>
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <SubmitButton
                    type="primary"
                    htmlType="submit"
                    block
                    loading={submitting}
                  >
                    Submit application
                  </SubmitButton>
                </Form.Item>
              </StyledForm>
            </>
          )}
        </Card>
        <FooterNote>
          Property owner?{" "}
          <Link to="/">Learn more about {APP_NAME}</Link>
        </FooterNote>
      </Shell>
    </Page>
  );
};

export default VendorApplyPage;
